import React from "react";
import { IoClose } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../helper/formatNumber";
import { formatCurrency } from "../../helper/formatCurrency";
import { useVariantUnitLabel } from "../../helper/variants";

const CartItems = ({
  items,
  itemQuantities,
  updateQuantity,
  removeItem,
  handleNoteChange,
  locale,
  isReadonly = false,
}) => {
  const { t, i18n } = useTranslation();
  const unitLabel = useVariantUnitLabel(i18n.language);

  const getLocalizedText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object")
      return field[i18n.language] || field.en || "";
    return "";
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "queued":
        return {
          label: t("queued") || "Queued",
          color: "bg-blue-100 text-blue-800 border-blue-300",
          icon: "feather-clock",
        };
      case "preparing":
        return {
          label: t("preparing") || "Preparing",
          color: "bg-yellow-100 text-yellow-800 border-yellow-300",
          icon: "feather-loader",
        };
      case "completed":
        return {
          label: t("completed") || "Ready",
          color: "bg-purple-100 text-purple-800 border-purple-300",
          icon: "feather-check",
        };
      case "delivered":
        return {
          label: t("delivered") || "Delivered",
          color: "bg-green-100 text-green-800 border-green-300",
          icon: "feather-check-circle",
        };
      default:
        return {
          label: t("pending") || "Pending",
          color: "bg-gray-100 text-gray-800 border-gray-300",
          icon: "feather-circle",
        };
    }
  };

  const containerClass = isReadonly
    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 shadow-sm"
    : "bg-gradient-to-r from-brand-50 to-brand-50 border-2 border-brand-200 hover:shadow-md transition-all position-relative";

  const borderClass = isReadonly ? "border-green-300" : "border-brand-300";
  const priceColorClass = isReadonly
    ? "text-green-700 font-bold"
    : "text-brand-700 font-bold";
  const badgeClass = isReadonly
    ? "bg-green-200 text-green-800"
    : "bg-brand-200 text-brand-800";

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const variant = item.variants?.[0];
        // For editable lines, prefer the price already stored against this
        // cart line (it bakes in any selected add-ons); fall back to the
        // base product price for lines not yet tracked in itemQuantities.
        // Readonly (already-placed) lines always carry their saved total
        // directly on item.price.
        const price =
          itemQuantities[item._id]?.price ??
          variant?.offerPrice ??
          variant?.price ??
          item.offerPrice ??
          item.price;

        return (
          <div key={index} className={`p-3 rounded-xl ${containerClass} mb-3`}>
            {/* Status Badge for readonly items */}
            {isReadonly && (
              <div className="mb-2 d-flex gap-2 align-items-center flex-wrap">
                <span
                  className={`${badgeClass} px-2 py-1 rounded-full text-xs font-semibold`}
                >
                  <i
                    className="feather-check"
                    style={{ fontSize: "0.75rem" }}
                  ></i>{" "}
                  {t("ordered") || "Ordered"}
                </span>
                {item.status && (
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusConfig(item.status).color}`}
                  >
                    <i
                      className={`${getStatusConfig(item.status).icon}`}
                      style={{ fontSize: "0.75rem" }}
                    ></i>{" "}
                    {getStatusConfig(item.status).label}
                  </span>
                )}
              </div>
            )}

            {/* Remove Button - Only for editable items */}
            {!isReadonly && (
              <button
                onClick={() => removeItem(item._id)}
                className="position-absolute btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  top: "0.35rem",
                  right: "0.35rem",
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#dc2626",
                  zIndex: 10,
                }}
                title={t("removeItem")}
              >
                <IoClose style={{ fontSize: "16px" }} />
              </button>
            )}

            <div className="d-flex align-items-start gap-2">
              {/* Product Image */}
              {item.productImage && (
                <img
                  src={item.productImage}
                  alt={getLocalizedText(item.productName)}
                  className={`rounded-xl object-cover flex-shrink-0 ${isReadonly ? "border-2 border-green-300" : "border-2 border-brand-300"}`}
                  style={{ width: "70px", height: "70px" }}
                />
              )}

              {/* Product Info */}
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <h6
                  className="mb-1 font-bold text-gray-900"
                  style={{
                    fontSize: "0.95rem",
                    paddingRight: isReadonly ? "0" : "1.2rem",
                    lineHeight: "1.4",
                  }}
                >
                  {getLocalizedText(item.productName)}
                </h6>
                {!isReadonly && item.variantUnit && (
                  <span className="d-inline-block mb-1 px-2 py-0 rounded-pill bg-white border border-orange-200 text-orange-700 text-xs font-semibold">
                    {unitLabel(item.variantUnit)}
                  </span>
                )}
                <p
                  className={`${priceColorClass} mb-1`}
                  style={{ fontSize: "0.9rem" }}
                >
                  {formatCurrency(price, locale)}
                  {isReadonly && (
                    <span className="text-gray-500 font-normal text-xs ml-2">
                      × {formatNumber(item.quantity || 1, i18n.language)}
                    </span>
                  )}
                </p>
                {item.addOns?.length > 0 && (
                  <p
                    className="text-gray-500 mb-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    +{" "}
                    {item.addOns
                      .map((a) => (a.quantity > 1 ? `${a.name} x${a.quantity}` : a.name))
                      .join(", ")}
                  </p>
                )}
                {isReadonly && item.note && (
                  <div className="bg-white/70 rounded-lg p-2 mt-2">
                    <p
                      className="text-gray-700 mb-0"
                      style={{ fontSize: "0.8rem" }}
                    >
                      <i
                        className="feather-message-circle text-gray-500"
                        style={{ fontSize: "0.75rem" }}
                      ></i>{" "}
                      {item.note}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Note Input - Only for editable (not-yet-placed) items */}
            {!isReadonly && (
              <div className="mt-2">
                <input
                  type="text"
                  placeholder={t("addNote") || "Add a note..."}
                  value={item.note || ""}
                  onChange={(e) => handleNoteChange(item._id, e.target.value)}
                  className="form-control form-control-sm"
                  style={{ fontSize: "0.8rem" }}
                />
              </div>
            )}

            {/* Quantity Controls and Price */}
            <div
              className={`d-flex justify-content-between align-items-center mt-3 pt-3 border-top ${borderClass}`}
            >
              {isReadonly ? (
                <div className="d-flex align-items-center gap-2">
                  <div className="bg-green-200/50 px-3 py-1 rounded-full">
                    <span
                      className="text-green-800 font-bold"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {t("qty") || "Qty"}:{" "}
                      {formatNumber(item.quantity || 1, i18n.language)}
                    </span>
                  </div>
                </div>
              ) : (
                <div
                  className="btn-group rounded-lg overflow-hidden"
                  style={{ border: "1.5px solid rgb(var(--brand-200))" }}
                >
                  <button
                    type="button"
                    className="btn btn-sm btn-light border-0 text-brand-600"
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "0.3rem 0.6rem",
                    }}
                    onClick={() => {
                      if (itemQuantities[item._id]?.quantity > 0) {
                        const newQuantity =
                          itemQuantities[item._id].quantity - 1;
                        updateQuantity(item._id, newQuantity, price);
                        if (newQuantity === 0) removeItem(item._id);
                      }
                    }}
                  >
                    <i className="feather-minus"></i>
                  </button>
                  <span
                    className="btn btn-sm bg-brand-50 border-0 text-gray-800 font-semibold"
                    style={{
                      minWidth: "42px",
                      pointerEvents: "none",
                      padding: "0.3rem 0.6rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {formatNumber(
                      itemQuantities[item._id]?.quantity || 0,
                      i18n.language,
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border-0 text-brand-600"
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "0.3rem 0.6rem",
                    }}
                    onClick={() => {
                      const currentQuantity =
                        itemQuantities[item._id]?.quantity || 0;
                      updateQuantity(item._id, currentQuantity + 1, price);
                    }}
                  >
                    <i className="feather-plus"></i>
                  </button>
                </div>
              )}

              <div
                className={`font-bold ${isReadonly ? "text-green-700 bg-green-100 px-3 py-1 rounded-lg" : "text-gray-900"}`}
                style={{ fontSize: "1rem" }}
              >
                {formatCurrency(
                  price *
                    (itemQuantities[item._id]?.quantity || item.quantity || 0),
                  locale,
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CartItems;
